import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useToasts } from '../../../contexts/toasts';
import { Alert, Card, Col, Container, Row } from 'react-bootstrap';
import AppSearch from '../../../components/common/AppSearch';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Llm, LlmModel } from '../../../api/Llm';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { sectionLabelsSelector } from '../../../slices/sectionsSlice';
import AppLoader from '../../../components/common/AppLoader';
import { Flex, Tag } from 'antd';
import { formatDate } from '../../../utils';


export default function ModelsList() {
  const [models, setModels] = useState<LlmModel[]>();
  const [error, setError] = useState(false);
  const [searchText, setSearchText] = useState('');

  const { showToast } = useToasts();

  const filteredModels = useMemo(() => models?.filter(m => m.name.toLowerCase().includes(searchText.toLowerCase())), [models, searchText]);
  const modelLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "llm-models"));

  const initModels = useCallback(() => {
    Llm.getModels()
      .then(
        ({ data }) => setModels(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('models-error', message, `${modelLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        });
  }, [showToast, modelLabels]);

  useEffect(() => initModels(), [initModels]);

  const handleSearchChange = useCallback((value: string) => setSearchText(value), []);
  const debounceSearchChange = useMemo(() => debounce(handleSearchChange, 500), [handleSearchChange]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!filteredModels) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return filteredModels.length === 0
      ? (
        <Alert className="py-2" variant="info">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {modelLabels.plural} found.</span>
        </Alert>
      )
      : (
        <Row>
          {filteredModels?.map(model => (
            <Col key={model.model} md={3} className="px-2">
              <Card className="shadow-sm mb-3">
                <Flex justify="space-between" className="pt-1 px-1">
                  <small className="text-muted">{formatDate(model.modified_at)}</small>
                  <Tag color="magenta" className="font-sm fw-bold me-0">{model.details?.parameter_size}</Tag>
                </Flex>
                <Card.Body className="py-2 mb-2 text-center">
                  <h6 className="font-lg mb-0">{model.name}</h6>
                  <Tag color="blue" className="font-sm fw-bold">{model.details?.family}</Tag>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )
  }, [error, filteredModels, modelLabels]);

  return (
    <Container className="mt-3">
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">{modelLabels.plural} List</h4>
        </Col>

        <Col>
          <AppSearch placeholder={`Search ${modelLabels.plural}...`} onChange={debounceSearchChange} />
        </Col>

        <Col xs={6} md={4} className="text-end"></Col>
      </Row>

      {renderAsyncContent()}
    </Container>
  )
}